// ABOUTME: The one entity_type/type → destination resolver for notification
// deep-links (#2209). Both the bell dropdown and /notifications route through
// this module, so the two surfaces cannot drift.
//
// Two layers, because notifications come in two shapes. A row ABOUT something
// (a draft, a lead, a campaign) carries an entity_type and routes on it, so a
// new type is clickable with no edit here. A row about an event with no single
// addressable entity — a sync across every account — has
// entity_type NULL, and routes on its `type` via TYPE_ROUTES. A CI gate
// (tests/unit/test_notification_route_coverage.py) fails when a NotificationType
// is missing from that map, so a new type cannot ship dead.

import { isFeatureHidden } from '@/config/features'
import { useUiStore } from '@/stores/useUiStore'
import { CHAT_THREAD_PATH } from '@/hooks/useIsChatHome'

/**
 * What clicking a notification should do. Chat is a slide-over panel, not a
 * routed page, so chat-bound notifications open the panel instead of
 * navigating (`useUiStore.setChatPanelOpen`).
 *
 * `none` means "this row has no destination" — the surfaces render it as
 * explicitly non-interactive (no pointer cursor, no role="button", not
 * focusable) rather than accepting a click that goes nowhere. The old
 * behaviour navigated to `/notifications`, which from the notifications page
 * itself is a navigation to the page you are already on: a no-op the user
 * cannot tell apart from a broken link (#2209).
 */
export type NotificationRoute =
  | { kind: 'navigate'; to: string }
  | { kind: 'chat-panel' }
  | { kind: 'none' }

/** The minimal slice of a Notification the resolver reads. */
export interface RoutableNotification {
  type: string
  entity_type?: string | null
  entity_id?: string | null
  payload?: Record<string, unknown>
}

function navigate(to: string): NotificationRoute {
  return { kind: 'navigate', to }
}

function chatPanel(): NotificationRoute {
  return { kind: 'chat-panel' }
}

// Approvals surface as cards in the Marketing chat thread. Card focus is a
// later `?focus=<approval_id>` suffix on this one route.
function approvalsRoute(): NotificationRoute {
  return navigate('/chat')
}

/**
 * A finished/failed Hermes run (`dispatcher.py`'s `AGENT_RUN_COMPLETED`/
 * `AGENT_RUN_FAILED`, entity_type `agent_run`). An in-app-channel run's own
 * reply IS the chat turn the user was waiting on (`dispatcher.py` writes it
 * back as a `hermes_conversation_messages` row and stamps `message_id` in the
 * payload) — that reply is only visible in chat, so the tap opens the chat
 * panel. Any other channel (WhatsApp/Slack/email) has no in-app widget to
 * point at yet, so it lands on Today, where finished agent activity surfaces
 * under "Results This Week"/"Needs You".
 */
function agentRunRoute(_id: string | null, n: RoutableNotification): NotificationRoute {
  return n.payload?.channel === 'in_app' ? chatPanel() : navigate('/approvals')
}

/** Render and conformance events open the final-cut screen of their project. */
function videoEditorRoute(n: RoutableNotification): NotificationRoute {
  const projectId = n.payload?.project_id
  return navigate(
    typeof projectId === 'string' && projectId ? `/content/video-editor/${projectId}` : '/content'
  )
}

const VIDEO_RENDER_TYPES: ReadonlySet<string> = new Set([
  'video_render_started',
  'video_render_completed',
  'video_render_failed',
  'video_conformance_started',
  'video_conformance_completed',
  'video_conformance_failed',
])

/**
 * Every entity_type the backend writes (`grep 'entity_type="' app/`), mapped
 * to the route that shows that entity. `id` is the notification's entity_id —
 * detail routes use it, list pages ignore it.
 */
export const ENTITY_ROUTES: Record<
  string,
  (id: string | null, n: RoutableNotification) => NotificationRoute
> = {
  // Content
  content_draft: (id) => navigate(id ? `/content/post/${id}` : '/content'),
  content_plan: () => navigate('/calendar'),
  content_generation: () => navigate('/content'),
  video_agent_job: () => navigate('/content'),
  video_render: (_id, n) => videoEditorRoute(n),
  video_job: (_id, n) => videoEditorRoute(n),
  video_project: (id) => navigate(id ? `/content/video-editor/${id}` : '/content'),
  media_asset: () => navigate('/media'),
  review_page: (id) => navigate(id ? `/review/${id}` : '/approvals'),
  music_track: () => navigate('/media'),
  voice_clone: () => navigate('/settings/voice-clone'),
  // Agent / chat
  agent_task: () => navigate(CHAT_THREAD_PATH),
  scheduled_task: () => navigate(CHAT_THREAD_PATH),
  agent_approval: () => approvalsRoute(),
  agent_run: agentRunRoute,
  // CRM / inbox
  lead: (id) => navigate(id ? `/leads/${id}` : '/leads'),
  contact: () => navigate('/crm/contacts'),
  conversation_message: () => navigate('/inbox'),
  inbox_reply: () => navigate('/inbox'),
  messaging_config: () => navigate('/settings/advanced#sending'),
  sms_campaign: () => navigate('/campaigns'),
  // Ads
  ad_campaign: (id) => navigate(id ? `/ads/campaigns/${id}` : '/ads/campaigns'),
  ad: () => navigate('/ads/campaigns'),
  // GMB (reviews is the /gmb landing; Q&A has no page of its own)
  gmb_location: () => navigate('/inbox?tab=reviews'),
  gmb_review: () => navigate('/inbox?tab=reviews'),
  gmb_question: () => navigate('/inbox?tab=reviews'),
  // Billing
  subscription: () => navigate('/settings/plan#plan'),
  invoice: () => navigate('/settings/plan#invoices'),
  credit_balance: () => navigate('/settings/plan#credits'),
  promo_code_redemption: () => navigate('/settings/plan#credits'),
  // Analytics
  report: () => navigate('/results'),
  feedback: () => navigate('/dashboard'),
  // Accounts / integrations / business
  connected_account: () => navigate('/settings/accounts'),
  oauth_grant: () => navigate('/settings/advanced#mcp'),
  business_api_key: () => navigate('/settings/advanced#mcp'),
  business: () => navigate('/settings/business'),
  shopify_order: () => navigate('/shopify'),
  // Admin: a finished evaluation run opens in the Eval runs gallery.
  eval_run: (id) => navigate(id ? `/admin?tab=evals&run=${id}` : '/admin?tab=evals'),
}

/**
 * Every `NotificationType` the backend can emit → where that KIND of event is
 * seen, for rows that carry no addressable entity of their own.
 *
 * 27% of production notification rows have `entity_type IS NULL` — a sync that
 * spans every connected account. Those are not missing data: there is no single row to link to. Before
 * this map they fell through to a `/notifications` fallback, which is the
 * no-op #2209 was reopened about. The type always exists, so the type always
 * resolves.
 *
 * Exhaustive over `NotificationType` by CI gate
 * (`tests/unit/test_notification_route_coverage.py`), which is what stops a
 * newly added type shipping dead. Values are router paths.
 */
export const TYPE_ROUTES: Record<string, string> = {
  // --- Leads / CRM / inbox ---
  lead_subscription_fired: '/leads',
  contact_deleted: '/crm/contacts',
  contacts_merged: '/crm/contacts',
  lead_deleted: '/leads',
  lead_phone_shared: '/leads',
  auto_response_fired: '/inbox',
  auto_response_failed: '/inbox',
  inbox_reply_sent: '/inbox',
  inbox_reply_send_failed: '/inbox',
  inbox_reply_assisted: '/inbox',
  inbox_auto_send_held: '/inbox',
  inbox_auto_send_started: '/inbox',
  inbox_auto_send_completed: '/inbox',
  inbox_auto_send_failed: '/inbox',
  engagement_drafts_ready: '/inbox',
  engagement_session_done: '/inbox?tab=handled',
  engagement_day_summary: '/inbox?tab=handled',
  engagement_cap_reached: '/inbox',
  engagement_platform_blocked: '/inbox',
  engagement_reconnect_needed: '/settings/accounts',
  // --- Content ---
  concepts_ready: '/content',
  content_concept_created: '/content',
  content_plan_failed: '/content',
  content_published: '/content',
  content_draft_deleted: '/content',
  live_post_edited: '/content',
  content_failed: '/content',
  publish_failed: '/content',
  publish_started: '/content',
  publish_completed: '/content',
  publish_skipped_needs_reauth: '/content',
  publish_attempt_unsettled: '/content',
  publish_volume_cap_reached: '/content',
  publish_binding_invalidated: '/content',
  people_consent_required: '/content',
  people_consent_granted: '/content',
  people_consent_cleared: '/content',
  publish_held_for_consent: '/content',
  post_recovery_attempted: '/content',
  post_recovery_failed: '/content',
  content_scheduled: '/calendar',
  content_schedule_failed: '/calendar',
  content_rescheduled: '/calendar',
  content_cancelled: '/calendar',
  // --- Images / video / media ---
  image_edit_started: '/content',
  image_edit_completed: '/content',
  image_edit_failed: '/content',
  image_generation_started: '/content',
  image_generation_completed: '/content',
  image_generation_failed: '/content',
  video_started: '/content',
  video_completed: '/content',
  video_failed: '/content',
  video_analysis_started: '/content',
  video_analysis_completed: '/content',
  video_analysis_failed: '/content',
  video_edl_updated: '/content',
  video_render_started: '/content',
  video_render_completed: '/content',
  video_render_failed: '/content',
  video_conformance_started: '/content',
  video_conformance_completed: '/content',
  video_conformance_failed: '/content',
  video_job_started: '/content',
  video_job_completed: '/content',
  video_job_failed: '/content',
  assembly_started: '/content',
  assembly_completed: '/content',
  assembly_failed: '/content',
  auto_compose_started: '/content',
  auto_compose_completed: '/content',
  auto_compose_failed: '/content',
  media_upload_started: '/media',
  media_upload_completed: '/media',
  media_upload_failed: '/media',
  media_ingest_started: '/media',
  media_ingest_completed: '/media',
  media_ingest_failed: '/media',
  drive_copy_started: '/settings/accounts',
  drive_copy_completed: '/settings/accounts',
  drive_copy_failed: '/settings/accounts',
  drive_folder_recreated: '/settings/accounts',
  media_intake_ready: '/media',
  media_analysis_started: '/media',
  media_analysis_completed: '/media',
  media_analysis_failed: '/media',
  voice_clone_started: '/settings/voice-clone',
  voice_clone_completed: '/settings/voice-clone',
  voice_clone_failed: '/settings/voice-clone',
  voice_clone_deleted: '/settings/voice-clone',
  voice_clone_unavailable: '/settings/voice-clone',
  outside_edit_started: '/library',
  outside_edit_completed: '/library',
  outside_edit_failed: '/library',
  decision_backend_unavailable: '/media',
  // --- Agent fleet (#2558) — one Hermes machine per business on Fly.io.
  // Placeholder destination: there is no business-facing Agent panel yet
  // (that's the Agents-tab admin dashboard, a later PR of this same issue),
  // so these route to the business page for now rather than shipping dead.
  agent_machine_provision_started: '/settings/team#marketing',
  agent_machine_provision_completed: '/settings/team#marketing',
  agent_machine_provision_failed: '/settings/team#marketing',
  agent_machine_wake_started: '/settings/team#marketing',
  agent_machine_wake_completed: '/settings/team#marketing',
  agent_machine_wake_failed: '/settings/team#marketing',
  agent_machine_suspend_started: '/settings/team#marketing',
  agent_machine_suspend_completed: '/settings/team#marketing',
  agent_machine_suspend_failed: '/settings/team#marketing',
  agent_machine_destroy_started: '/settings/team#marketing',
  agent_machine_destroy_completed: '/settings/team#marketing',
  agent_machine_destroy_failed: '/settings/team#marketing',
  agent_machine_upgrade_started: '/settings/team#marketing',
  agent_machine_upgrade_completed: '/settings/team#marketing',
  agent_machine_upgrade_failed: '/settings/team#marketing',
  agent_machine_key_rotation_completed: '/settings/team#marketing',
  agent_machine_key_rotation_failed: '/settings/team#marketing',
  agent_machine_repair_started: '/settings/team#marketing',
  agent_machine_repair_completed: '/settings/team#marketing',
  agent_machine_repair_failed: '/settings/team#marketing',
  // Event-bus dispatcher (#2562). completed/failed carry entity_type
  // "agent_run" and resolve through ENTITY_ROUTES.agent_run instead (chat for
  // an in-app reply, Today otherwise) — these two only cover a row that
  // somehow arrives without an entity_id. started/cancelled have no
  // business-facing panel yet, so they fall back to Today too.
  agent_run_started: '/approvals',
  agent_run_completed: '/approvals',
  agent_run_failed: '/approvals',
  agent_run_retrying: '/approvals',
  agent_run_cancelled: '/approvals',
  agent_run_waiting: '/approvals',
  // Dispatch pause / event expiry: the business's own agent status.
  agent_dispatch_paused: '/settings/team#marketing',
  agent_dispatch_resumed: '/settings/team#marketing',
  agent_events_expired: '/settings/team#marketing',
  // Rendered-context sync onto the agent's machine.
  agent_context_sync_started: '/settings/team#marketing',
  agent_context_sync_completed: '/settings/team#marketing',
  agent_context_sync_failed: '/settings/team#marketing',
  // Producer-role seeding: the owner's own agent status; the backfill's run
  // notifications go to admins only.
  system_roles_seed_failed: '/settings/team#marketing',
  system_roles_backfill_started: '/dashboard',
  system_roles_backfill_completed: '/dashboard',
  system_roles_backfill_failed: '/dashboard',
  // AI spend-cap surfaces (#2570). messages-held normally carries
  // entity_type "conversation_message" and routes to /inbox via
  // ENTITY_ROUTES; this is only the fallback for a row with no entity_id.
  ai_spend_cap_reached: '/settings/plan#plan',
  ai_spend_cap_messages_held: '/inbox',
  // Task-allowance run-out rule (#2570) — same placeholder rationale:
  // normally carries entity_type "conversation_message" and routes to
  // /inbox via ENTITY_ROUTES.
  task_allowance_message_routed: '/inbox',
  // Ops console lifecycle automation (#2572) — same placeholder rationale.
  agent_machine_lifecycle_cancelled: '/settings/team#marketing',
  agent_machine_lifecycle_restored: '/settings/team#marketing',
  agent_machine_retention_deleted: '/settings/team#marketing',
  // Unused-machine reap sweep (#2571) — same placeholder rationale.
  agent_machine_reap_suspended: '/settings/team#marketing',
  agent_machine_reap_warning: '/settings/team#marketing',
  agent_machine_reap_deleted: '/settings/team#marketing',
  // Orphaned agent-volume sweep — admin-facing, same placeholder rationale.
  agent_volume_sweep_removed: '/dashboard',
  agent_volume_sweep_failed: '/dashboard',
  // Orphaned agent-machine sweep and failed machine teardown — admin-facing.
  agent_machine_sweep_found: '/dashboard',
  agent_machine_sweep_removed: '/dashboard',
  agent_machine_sweep_failed: '/dashboard',
  agent_machine_teardown_failed: '/dashboard',
  // Trial abuse limits (#2570) — same placeholder rationale.
  business_suspended: '/settings/business',
  business_unsuspended: '/settings/business',
  // Crew identity (#2818) — crew are managed beside the team.
  crew_member_added: '/settings/team#people',
  crew_member_verified: '/settings/team#people',
  crew_member_revoked: '/settings/team#people',
  crew_otp_failed: '/settings/team#people',
  crew_upload_failed: '/settings/team#people',
  // Group relay — the group's thread is in chat.
  sender_upload_link_sent: '/library',
  sender_upload_received: '/library',
  channel_delivery_failed: '/settings/accounts#channels',
  production_brief_started: CHAT_THREAD_PATH,
  production_brief_completed: CHAT_THREAD_PATH,
  production_brief_retrying: CHAT_THREAD_PATH,
  production_brief_failed: CHAT_THREAD_PATH,
  outreach_campaign_started: '/leads?outreach=campaigns',
  outreach_campaign_paused: '/leads?outreach=campaigns',
  outreach_run_started: '/leads',
  outreach_run_completed: '/leads',
  outreach_run_failed: '/leads',
  outreach_reply_received: '/inbox',
  outreach_opted_out: '/leads',
  outreach_email_sent: '/leads?outreach=campaigns',
  outreach_email_failed: '/leads?outreach=campaigns',
  outreach_mailbox_paused: '/settings/team#outreach',
  outreach_role_enabled: '/settings/team#outreach',
  outreach_role_disabled: '/settings/team#outreach',
  outreach_template_saved: '/leads?outreach=campaigns',
  outreach_template_archived: '/leads?outreach=campaigns',
  marketing_task_received: '/chat',
  marketing_task_awaiting_approval: '/approvals',
  marketing_task_completed: '/chat',
  marketing_task_failed: '/chat',
  marketing_task_cancelled: '/chat',
  marketing_task_webhook_started: '/chat',
  marketing_task_webhook_completed: '/chat',
  marketing_task_webhook_failed: '/chat',
  video_run_failed: '/approvals',
  video_run_cost_cap_exceeded: '/approvals',
  post_plan_created: '/library',
  post_plan_schedule_overridden: '/library',
  post_plan_downgraded: '/library',
  media_cleanup_completed: '/media',
  media_cleanup_failed: '/media',
  preview_link_created: '/library?section=content',
  review_page_ready: '/approvals',
  review_page_approved: '/approvals',
  review_page_sent_back: '/approvals',
  review_note_replied: '/approvals',
  review_keyframes_started: '/approvals',
  review_keyframes_completed: '/approvals',
  review_keyframes_failed: '/approvals',
  variation_created: '/library?section=content',
  variation_revised: '/library?section=content',
  variation_picked: '/library?section=content',
  variation_expired: '/library?section=content',
  variation_purge_started: '/library?section=content',
  variation_purge_completed: '/library?section=content',
  variation_purge_failed: '/library?section=content',
  music_generation_started: '/media',
  music_generation_completed: '/media',
  music_generation_failed: '/media',
  music_bed_started: '/media',
  music_bed_completed: '/media',
  music_bed_failed: '/media',
  // --- Chat / agent ---
  agent_loop_detected: '/chat',
  agent_conversation_ceiling_reached: '/chat',
  memory_recipe_completed: '/chat',
  memory_recipe_failed: '/chat',
  memory_rollup_failed: '/chat',
  memory_preference_saved: '/settings/profile#privacy',
  memory_preference_deleted: '/settings/profile#privacy',
  // --- Approvals / MCP access ---
  agent_approval: '/chat',
  agent_approval_pending: '/chat',
  approval_expired: '/approvals',
  approval_args_mismatch: '/approvals',
  mcp_key_new_location: '/settings/advanced#mcp',
  mcp_key_business_switched: '/settings/advanced#mcp',
  mcp_key_rehomed: '/settings/advanced#mcp',
  mcp_key_revoked: '/settings/advanced#mcp',
  agent_platform_connected: '/settings/advanced#agent-platforms',
  ssh_host_added: '/settings/advanced#ssh',
  tailscale_connect_started: '/settings/advanced#tailscale',
  tailscale_connected: '/settings/advanced#tailscale',
  tailscale_needs_attention: '/settings/advanced#tailscale',
  tailscale_disconnected: '/settings/advanced#tailscale',
  own_browser_enabled: '/settings/accounts#own-browser',
  own_browser_disabled: '/settings/accounts#own-browser',
  own_browser_session_started: '/settings/accounts#own-browser',
  own_browser_session_ended: '/settings/accounts#own-browser',
  browser_session_ended: '/settings/accounts#own-browser',
  browser_session_failed: '/settings/accounts#own-browser',
  ai_account_connected: '/settings/advanced#ai-account',
  ai_account_disconnected: '/settings/advanced#ai-account',
  agent_platform_revoked: '/settings/advanced#agent-platforms',
  agent_platform_spend_cap_reached: '/settings/advanced#agent-platforms',
  // --- Campaigns / messaging ---
  sms_campaign_started: '/campaigns',
  sms_campaign_completed: '/campaigns',
  sms_campaign_failed: '/campaigns',
  messaging_audience_drift_aborted: '/campaigns',
  messaging_quiet_hours_deferred: '/campaigns',
  messaging_frequency_cap_blocked: '/campaigns',
  messaging_provision_started: '/settings/advanced#sending',
  messaging_provision_completed: '/settings/advanced#sending',
  messaging_provision_failed: '/settings/advanced#sending',
  // --- Ads ---
  ads_sync_started: '/ads/campaigns',
  ads_sync_completed: '/ads/campaigns',
  ads_sync_failed: '/ads/campaigns',
  ad_spend_cap_reached: '/ads/campaigns',
  google_ads_link_started: '/ads/settings',
  google_ads_link_completed: '/ads/settings',
  google_ads_link_failed: '/ads/settings',
  // --- Google Business Profile ---
  gmb_review_sync_failed: '/inbox?tab=reviews',
  gmb_qa_sync_failed: '/inbox?tab=reviews',
  gmb_sync_disabled: '/inbox?tab=reviews',
  gmb_insights_sync_failed: '/gmb/insights',
  // --- YouTube comments ---
  youtube_comment_sync_completed: '/inbox',
  youtube_comment_sync_failed: '/settings/accounts',
  youtube_feature_blocked: '/settings/accounts',
  x_mention_sync_completed: '/inbox',
  x_mention_sync_failed: '/settings/accounts',
  // --- Business profile / knowledge base / connections ---
  business_profile_updated: '/settings/business',
  extraction_started: '/settings/business#about',
  extraction_completed: '/settings/business#about',
  extraction_failed: '/settings/business#about',
  brand_kit_confirmed: '/settings/business#brand-kit',
  brand_marks_captured: '/settings/business#brand-kit',
  brand_marks_failed: '/settings/business#brand-kit',
  token_expired: '/settings/accounts',
  token_expiring_soon: '/settings/accounts',
  oauth_token_refreshed: '/settings/accounts',
  oauth_token_refresh_failed: '/settings/accounts',
  account_connect_failed: '/settings/accounts',
  meta_account_disconnected: '/settings/accounts',
  // Content import deep-links to connections, where an unreadable platform is fixed.
  content_import_started: '/settings/accounts',
  content_import_completed: '/settings/accounts',
  content_import_failed: '/settings/accounts',
  content_analysis_started: '/results?tab=content_performance',
  content_analysis_completed: '/results?tab=content_performance',
  content_analysis_failed: '/results?tab=content_performance',
  learning_refresh_started: '/results?tab=post_results',
  learning_refresh_completed: '/results?tab=post_results',
  learning_refresh_failed: '/results?tab=post_results',
  storage_sweep_started: '/library',
  storage_sweep_completed: '/library',
  storage_sweep_failed: '/library',
  // --- Billing ---
  budget_alert: '/settings/plan#credits',
  low_credits: '/settings/plan#credits',
  paid_call_refused: '/settings/plan#credits',
  paid_call_hold_expired: '/settings/plan#credits',
  credits_purchased: '/settings/plan#credits',
  credits_renewed: '/settings/plan#credits',
  // Task top-up purchase (#2570) — the buy button lives on the plans page.
  task_topup_purchased: '/settings/plan#plan',
  // NOT /billing/credits: the balance is fine when the agent's own daily
  // allowance runs out (#2008, AC-14), and landing the user on a billing page
  // pushes an upgrade at someone who only needs to wait for midnight UTC.
  // Chat is where the pause happened and where the explaining card renders.
  agent_credit_ceiling_reached: '/chat',
  agent_credit_burn_anomaly: '/settings/plan#credits',
  payment_failed: '/settings/plan#invoices',
  payment_succeeded: '/settings/plan#invoices',
  subscription_unpaid: '/settings/plan#invoices',
  subscription_created: '/settings/plan#plan',
  subscription_updated: '/settings/plan#plan',
  subscription_canceled: '/settings/plan#plan',
  subscription_trial_ended: '/settings/plan#plan',
  // Human-assisted setup (#2570): setup-call booking + workflow checklist.
  setup_call_requested: '/settings/plan#setup',
  setup_call_scheduled: '/settings/plan#setup',
  setup_checklist_item_verified: '/settings/plan#setup',
  // --- Analytics (the tab is read from the URL by AnalyticsPage) ---
  social_metrics_sync_started: '/results?tab=social',
  social_metrics_sync_completed: '/results?tab=social',
  social_metrics_sync_failed: '/results?tab=social',
  metrics_sync_failed: '/results?tab=social',
  // QuickBooks connector card lives under Settings › Accounts › More tools; there's no
  // dedicated QuickBooks page yet (that lands with the read/write tools).
  quickbooks_snapshot_started: '/settings/accounts#more-tools',
  quickbooks_snapshot_completed: '/settings/accounts#more-tools',
  quickbooks_snapshot_failed: '/settings/accounts#more-tools',
  quickbooks_sync_failed: '/settings/accounts#more-tools',
  prospect_run_started: '/leads',
  prospect_run_completed: '/leads',
  prospect_run_failed: '/leads',
  prospecting_deleted: '/leads',
  prospect_purge_started: '/leads',
  prospect_purge_completed: '/leads',
  prospect_purge_failed: '/leads',
  offer_delivery_started: '/library?section=offers',
  offer_delivery_completed: '/library?section=offers',
  offer_delivery_failed: '/library?section=offers',
  business_events_key_rotated: '/settings/advanced#website-events',
  jobber_sync_started: '/settings/accounts#more-tools',
  jobber_sync_completed: '/settings/accounts#more-tools',
  jobber_sync_failed: '/settings/accounts#more-tools',
  jobber_disconnected: '/settings/accounts#more-tools',
  traffic_sync_started: '/results?tab=traffic',
  traffic_sync_completed: '/results?tab=traffic',
  traffic_sync_failed: '/results?tab=traffic',
  // --- Dashboard-level summaries ---
  feedback_submitted: '/dashboard',
  feedback_submit_failed: '/dashboard',
  feedback_sync_completed: '/dashboard',
  feedback_sync_failed: '/dashboard',
  system_alert: '/dashboard',
  // Nightly format priors rebuild: admin-only, fleet-wide.
  format_priors_started: '/admin',
  format_priors_completed: '/admin',
  format_priors_failed: '/admin',
  // Agent evaluation: admin-only, fleet-wide. A finished run carries entity_type
  // `eval_run` and opens that run; these land on the run list.
  eval_run_started: '/admin?tab=evals',
  eval_run_completed: '/admin?tab=evals',
  eval_run_failed: '/admin?tab=evals',
  eval_invariant_violation: '/admin',
  // The decisions' own OpenRouter key near its limit, refused or unset: admin-only.
  decision_key_alert: '/admin',
  // An Apify actor pinned behind its latest build, or a pin a platform turned away: admin-only.
  apify_pin_stale: '/admin',
  fleet_rollout_started: '/admin',
  fleet_rollout_completed: '/admin',
  fleet_rollout_failed: '/admin',
  // The review queue's "done" outcomes; each carries its entity and routes on it first.
  lead_received: '/leads',
  approval_executed: '/approvals',
  agent_report: '/chat',
  push_send_failed: '/settings/profile#notifications',
  // --- Shopify ---
  shopify_order_received: '/shopify',
  shopify_webhook_subscribe_failed: '/shopify',
  shopify_app_uninstalled: '/shopify',
  shopify_post_generation_started: '/shopify',
  shopify_post_generation_completed: '/shopify',
  shopify_post_generation_failed: '/shopify',
  shopify_derived_image_started: '/shopify',
  shopify_derived_image_completed: '/shopify',
  shopify_derived_image_failed: '/shopify',
  shopify_sync_started: '/shopify',
  shopify_sync_completed: '/shopify',
  shopify_sync_failed: '/shopify',
  shopify_boost_execute_started: '/shopify',
  shopify_boost_execute_completed: '/shopify',
  shopify_boost_execute_failed: '/shopify',
}

/**
 * Top-level path segment → the `featureKey` App.tsx gates it behind.
 *
 * `HiddenFeatureRoute` renders `<Navigate to="/dashboard">` when the feature is
 * hidden, and `ads`/`attribution` are hidden by default (`config/features.ts`).
 * So without this, clicking "Ads sync failed" bounces you to the dashboard with
 * no explanation — a real route, resolving cleanly, landing nowhere near what
 * the row said. That is the same misleading landing as the old `/notifications`
 * fallback, so those rows are rendered non-clickable instead (#2209).
 *
 * Kept in sync with App.tsx by `tests/unit/test_notification_route_coverage.py`.
 */
export const FEATURE_GATED_SEGMENTS: Record<string, string> = {
  ads: 'ads',
  attribution: 'attribution',
}

function isHiddenDestination(to: string): boolean {
  const segment = to.split('?')[0].split('/').filter(Boolean)[0] ?? ''
  const featureKey = FEATURE_GATED_SEGMENTS[segment]
  return featureKey !== undefined && isFeatureHidden(featureKey)
}

/**
 * Resolve where a notification click should land.
 *
 * Order: the two type-specific behaviours that need the row's own id or a
 * query param, then the entity_type map (most specific — it can deep-link to
 * the exact draft/lead/campaign), then the type map (the kind of event, for
 * rows with no addressable entity), then `none`.
 *
 * `none` is reached by a row whose `type` is not a `NotificationType` — a
 * legacy row, or a string the backend never declared — and by one whose
 * destination sits behind a hidden feature. There is no fallback navigation:
 * landing on `/notifications` was indistinguishable from a dead link, so such
 * a row is rendered non-clickable instead.
 */
export function resolveNotificationRoute(n: RoutableNotification): NotificationRoute {
  const route = resolveDeclaredRoute(n)
  if (route.kind === 'navigate' && isHiddenDestination(route.to)) {
    return { kind: 'none' }
  }
  return route
}

function resolveDeclaredRoute(n: RoutableNotification): NotificationRoute {
  if (n.type === 'agent_approval_pending' || n.type === 'agent_approval') {
    return approvalsRoute()
  }
  if (VIDEO_RENDER_TYPES.has(n.type)) {
    return videoEditorRoute(n)
  }
  const entityRoute = n.entity_type ? ENTITY_ROUTES[n.entity_type] : undefined
  if (entityRoute) {
    return entityRoute(n.entity_id ?? null, n)
  }
  const typeRoute = TYPE_ROUTES[n.type]
  if (typeRoute) {
    return navigate(typeRoute)
  }
  return { kind: 'none' }
}

/** Whether a notification row should render as an interactive, clickable row. */
export function isNotificationClickable(n: RoutableNotification): boolean {
  return resolveNotificationRoute(n).kind !== 'none'
}

/**
 * Execute a resolved route. `navigateTo` is the surface's react-router
 * `navigate` — client-side, never `window.location.assign` (a full reload
 * drops SPA state and, under MSW, resets in-memory auth).
 */
export function followNotificationRoute(
  route: NotificationRoute,
  navigateTo: (to: string) => void
): void {
  if (route.kind === 'none') {
    return
  }
  if (route.kind === 'navigate') {
    navigateTo(route.to)
    return
  }
  useUiStore.getState().setChatPanelOpen(true)
}
